import stringify from 'qs/lib/stringify';
import { Toast } from 'vant';
import 'whatwg-fetch';
import router from '../router';

class HTTP {
  constructor(baseURL) {
    this.host = baseURL;
    this.headers = new Headers({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    this.baseInit = {
      mode: 'cors',
      credentials: 'include',
    };
  }

  /**
   * 发起请求（带QueryString）
   * @param {string} url
   * @param {object} [params = {}] - QueryString参数
   * @param {object} [headers = {}] - fetch的配置，详见@{link https://developer.mozilla.org/zh-CN/docs/Web/API/WindowOrWorkerGlobalScope/fetch|Fetch()}
   * @param {string} [method = 'GET'] - 请求方法
   * @returns {Promise<any>}
   */
  requestForQueryString(url, params, headers, method = 'GET', stringifyFlag) {
    return new Promise(async (resolve, reject) => {
      try {
        const querystring = stringify(params, { encode: stringifyFlag });
        const requestURL = `${this.host}${url}?${querystring}`;
        const requestHeaders = this.headers;
        const authorization = localStorage.getItem('accessToken');
        if (authorization) {
          requestHeaders.set('Authorization', `${'bearer '}${authorization}`);
        }
        if (headers) {
          Object.entries(headers).forEach(([key, value]) => {
            requestHeaders.set(key, value);
          });
        }
        const fetchInit = {
          method,
          headers: requestHeaders,
        };
        const requestInit = Object.assign({}, fetchInit, this.baseInit);
        const request = new Request(requestURL, requestInit);
        const response = await fetch(request);
        let responseData = null;
        if (response.headers.get('content-type').includes('json')) {
          responseData = await response.json();
        } else {
          responseData = response.text();
        }

        if (response.status < 400) {
          resolve(responseData);
        } else {
          HTTP.throwError(responseData);
          reject(responseData);
        }
      } catch (error) {
        HTTP.throwError(error);
        reject(error);
      }
    });
  }

  /**
   * 发起请求（带FormData）
   * @param {string} url
   * @param {object} [data = {}] - formData参数
   * @param {object} [headers = {}] - 额外的Headers
   * @param {string} [method = 'POST'] - 请求方法
   * @returns {Promise<any>}
   */
  requestForFormData(url, data, headers, method = 'POST') {
    return new Promise(async (resolve, reject) => {
      try {
        const requestURL = `${this.host}${url}`;
        const requestHeaders = this.headers;
        const authorization = localStorage.getItem('accessToken');
        if (authorization) {
          requestHeaders.set('Authorization', `${'bearer '}${authorization}`);
        }
        if (headers) {
          Object.entries(headers).forEach(([key, value]) => {
            requestHeaders.set(key, value);
          });
        }
        const fetchInit = {
          method,
          headers: requestHeaders,
          body: stringify(data),
        };
        const requestInit = Object.assign({}, fetchInit, this.baseInit);
        const request = new Request(requestURL, requestInit);
        const response = await fetch(request);
        const responseData = await response.json();
        if (response.status < 400) {
          resolve(responseData);
        } else {
          HTTP.throwError(responseData);
          reject(responseData);
        }
      } catch (error) {
        HTTP.throwError(error);
        reject(error);
      }
    });
  }

  /**
   * 抛出异常
   * @param error
   */
  static throwError(error) {
    // Failed to fetch
    if (error.message === 'Failed to fetch') {
      Toast.fail('网络请求异常');
      return;
    }
    if (error.message) {
      Toast.fail(error.message);
      if (error.message.includes('登陆') || error.message.includes('登录')) {
        router.replace({
          name: 'login',
          query: {
            redirect: window.location.href,
          },
        });
      }
    } else {
      Toast.fail('请求失败');
    }
  }

  /**
   * GET
   * @param {string} url
   * @param {object} [params = {}] - QueryString参数
   * @param {object} [headers = {}] - 额外的Headers
   * @returns {Promise<any>}
   */
  get(url, params = {}, headers = {}, stringifyFlag = true) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.requestForQueryString(url, params, headers, 'GET', stringifyFlag);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * POST
   * @param {string} url
   * @param {object} [data = {}] - formData参数
   * @param {object} [headers = {}] - 额外的Headers
   * @returns {Promise<any>}
   */
  post(url, data = {}, headers = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.requestForFormData(url, data, headers, 'POST');
        resolve(response);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * PUT
   * @param {string} url
   * @param {object} [data = {}] - formData参数
   * @param {object} [headers = {}] - 额外的Headers
   * @returns {Promise<any>}
   */
  put(url, data = {}, headers = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.requestForFormData(url, data, headers, 'PUT');
        resolve(response);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * DELETE
   * @param {string} url
   * @param {object} [params = {}] - QueryString参数
   * @param {object} [headers = {}] - 额外的Headers
   * @returns {Promise<any>}
   */
  delete(url, params = {}, headers = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.requestForQueryString(url, params, headers, 'DELETE');
        resolve(response);
      } catch (error) {
        reject(error);
      }
    });
  }
}

export default HTTP;
